import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MetaData } from '../interfaces/meta-data.interface';
import { MediaType, Path } from '../path.enum';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  constructor(private readonly meta: Meta, private readonly title: Title) {}

  setMetaTags(data: MetaData): void {
    const { title, description, image, mediaType, url } = data;
    // Construct og:url - use provided url or construct from path
    let ogUrl: string;
    if (url) {
      // If url starts with http, use as-is, otherwise prepend productUrl
      if (url.startsWith('http')) {
        ogUrl = url;
      } else {
        const urlPath = url.startsWith('/') ? url : '/' + url;
        ogUrl = `${environment.productUrl}${urlPath}`;
      }
    } else {
      // Fallback: construct from path enum
      const path =
        mediaType === MediaType.MOVIE ? Path.MOVIE_DETAIL : Path.WEB_SERIES;
      ogUrl = `${environment.productUrl}/${path}`;
    }

    // Ensure image URL is absolute and uses HTTPS
    let ogImage = image;
    if (ogImage && !ogImage.startsWith('http')) {
      ogImage = ogImage.startsWith('/')
        ? `${environment.productUrl}${ogImage}`
        : `${environment.productUrl}/${ogImage}`;
    }
    // Force HTTPS for og:image
    if (ogImage?.startsWith('http://')) {
      ogImage = ogImage.replace('http://', 'https://');
    }

    const ogTitle = title + ' | ABC Talkies';

    // Basic meta tags
    this.title.setTitle(title + ' | ABC Talkies');

    // Description
    this.meta.updateTag({
      name: 'description',
      content: description,
    });
    this.meta.updateTag({
      name: 'keyword',
      content: title,
    });

    // Open Graph tags (required for Facebook, WhatsApp, LinkedIn)
    this.meta.updateTag({
      property: 'og:type',
      content: mediaType === 'film' ? 'video.movie' : 'video.episode',
    });
    this.meta.updateTag({
      property: 'og:url',
      content: ogUrl,
    });
    this.meta.updateTag({
      property: 'og:title',
      content: ogTitle,
    });
    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });
    this.meta.updateTag({
      property: 'og:site_name',
      content: 'ABC Talkies',
    });
    this.meta.updateTag({
      property: 'og:locale',
      content: 'en_US',
    });

    // Image tags (critical for Facebook and WhatsApp)
    if (ogImage) {
      this.meta.updateTag({
        property: 'og:image',
        content: ogImage,
      });
      // og:image:secure_url is required for WhatsApp
      this.meta.updateTag({
        property: 'og:image:secure_url',
        content: ogImage,
      });
      this.meta.updateTag({
        property: 'og:image:alt',
        content: title,
      });
      // Recommended dimensions for Facebook/WhatsApp (1200x630 is optimal)
      // Note: We can't get actual dimensions without making a request,
      // but we set recommended values
      this.meta.updateTag({
        property: 'og:image:type',
        content: 'image/jpeg',
      });
      this.meta.updateTag({
        property: 'og:image:width',
        content: '1200',
      });
      this.meta.updateTag({
        property: 'og:image:height',
        content: '630',
      });
    }

    // Twitter Card tags (WhatsApp sometimes uses these)
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: ogTitle,
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
    if (ogImage) {
      this.meta.updateTag({
        name: 'twitter:image',
        content: ogImage,
      });
    }

    // Additional meta tags
    this.meta.updateTag({
      property: 'author',
      content: 'ABC Talkies',
    });

    // Facebook App ID (if configured)
    if (environment.fbAppId) {
      this.meta.updateTag({
        property: 'fb:app_id',
        content: environment.fbAppId,
      });
    }
  }
}
